import {
  Box,
  Checkbox,
  Fade,
  FormControlLabel,
  Grid,
  Typography,
} from '@mui/material';
import RoundBackgroundInput from '../input/RoundBackgroundInput';
import { images } from '../../assets/images';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { appEnvironment, appAutoCompleteData } from '../../common/environment';
import validator from '../../onboarding/common/validator';
import { checkoutActions } from '../../redux/checkout';
import { useAppDispatch } from '../../redux/store';
import PasswordStrengthView from '../input/PasswordStrengthView';
import { onboardingActions } from '../../redux/onboarding';
import { ContinueRef } from '../../onboarding/types';
import analyticsTracker from '../../common/analytics';
import { addDiscountCodeV2 } from '../../redux/checkoutActions';
import { useEmailAvailableMutation } from '../../api/api';
import { OnboardingV2Step } from '../../redux/onboardingTypes';
import { useMobile } from '../../common/hooks';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import i18n from '../../i18n';
import i18next from '../../i18n';
import { useSearchParams } from 'react-router-dom';

const SignUpForm = forwardRef<ContinueRef, unknown>((_props, ref) => {
  const { t } = useTranslation();

  useImperativeHandle(ref, () => ({
    continuePressed: async () => {
      if (!hasErrors) {
        const finalEmail = email.trim().toLowerCase();
        const { available } = await checkEmailAvailability({
          email: finalEmail,
        }).unwrap();
        if (!available) {
          setEmailError(t('emailIsAlreadyTaken'));
          return;
        }
        try {
          Sentry.setUser({
            email: finalEmail,
            name,
            lastName,
          });

          await dispatch(
            addDiscountCodeV2(
              coupon.length > 0 ? coupon : appEnvironment.defaultCoupon
            )
          ).unwrap();
          dispatch(
            checkoutActions.setPersonalData({
              name,
              lastName,
              email: finalEmail,
              password,
              code: coupon,
            })
          );
          dispatch(
            onboardingActions.setOnboardingStep(OnboardingV2Step.SECOND_FORM)
          );

          analyticsTracker.track('Web 1st step completed', { mobile });
        } catch (e) {
          setCouponError(t('invalidCoupon'));
        }
      }
      return {};
    },
  }));

  const language = i18n.language;

  const dispatch = useAppDispatch();

  const [params] = useSearchParams();

  const [checkEmailAvailability] = useEmailAvailableMutation();

  const [tcsAgreed, setTcsAgreed] = useState(
    appEnvironment.autocomplete ? true : false
  );

  const [passwordOn, setPasswordOn] = useState(true);

  const [name, setName] = useState(
    appEnvironment.autocomplete ? appAutoCompleteData.name : ''
  );
  const [lastName, setLastName] = useState(
    appEnvironment.autocomplete ? appAutoCompleteData.lastName : ''
  );
  const [coupon, setCoupon] = useState(
    appEnvironment.autocomplete
      ? appAutoCompleteData.coupon
      : params.get('code') || ''
  );
  const [email, setEmail] = useState(
    appEnvironment.autocomplete ? appAutoCompleteData.email : ''
  );
  const [password, setPassword] = useState(
    appEnvironment.autocomplete ? appAutoCompleteData.password : ''
  );

  const [nameError, setNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [couponError, setCouponError] = useState('');
  const [emailError, setEmailError] = useState('');

  const hasErrors = useMemo(() => {
    const trimmedEmail = email.trim();
    return (
      name.length === 0 ||
      lastName.length === 0 ||
      trimmedEmail.length === 0 ||
      !validator.validEmail(trimmedEmail) ||
      password.length === 0 ||
      !validator.passwordLength(password) ||
      !validator.passwordHasSpecialCharacters(password) ||
      !validator.passwordHasUppercaseAndLowercase(password) ||
      !validator.passwordHasDigit(password) ||
      emailError.length > 0 ||
      !tcsAgreed
    );
  }, [
    email,
    emailError.length,
    lastName.length,
    name.length,
    password,
    tcsAgreed,
  ]);

  useEffect(() => {
    dispatch(onboardingActions.setButtonEnabled(!hasErrors));
  }, [email, password, name, lastName, coupon, hasErrors, dispatch]);

  const mobile = useMobile();

  const isJa = (i18n.resolvedLanguage ?? i18n.language)?.startsWith('ja');

  const termsURI = useMemo(() => {
    const uri = appEnvironment.termsURL;

    if (isJa) {
      return `${uri}?lng=ja-JP`;
    } else if (i18next.language === 'ar') {
      return `${uri}?lng=ar-001`;
    } else {
      return uri;
    }
  }, [isJa]);

  return (
    <Fade in={true}>
      <Grid
        container
        className={mobile ? 'px-[16px]' : 'w-full'}
        rowGap={2}
        alignContent={mobile ? 'center' : undefined}
        sx={mobile ? { flex: 1, marginTop: '100px' } : {}}
      >
        <Grid
          container
          size={{ xs: 12 }}
          direction="row"
          justifyContent={'center'}
        >
          <Grid size={{ md: 6, xs: 12 }} alignSelf={'center'}>
            <RoundBackgroundInput
              placeholder={t('accessCode')}
              onChange={(e) => {
                setCouponError('');
                setCoupon(e.target.value);
              }}
              helperText={couponError}
              error={couponError.length > 0}
              value={coupon}
            />
          </Grid>
        </Grid>

        <Grid
          container
          size={{ xs: 12 }}
          direction={isJa ? 'row-reverse' : undefined}
          sx={isJa ? { flexDirection: 'row-reverse' } : undefined}
        >
          <Grid flex={1}>
            <RoundBackgroundInput
              placeholder={t('firstName')}
              onChange={(e) => {
                setNameError('');
                setName(e.target.value);
              }}
              onBlur={() => {
                if (name.length === 0) {
                  setNameError(t('pleaseCompleteThisField'));
                }
              }}
              error={nameError.length > 0}
              helperText={nameError}
              value={name}
            />
          </Grid>

          <Box
            component="img"
            src={images.form.nameInput.focused}
            className="mx-4"
          />

          <Grid flex={1}>
            <RoundBackgroundInput
              placeholder={t('lastName')}
              onChange={(e) => {
                setLastNameError('');
                setLastName(e.target.value);
              }}
              onBlur={() => {
                if (lastName.length === 0) {
                  setLastNameError(t('pleaseCompleteThisField'));
                }
              }}
              helperText={lastNameError}
              error={lastNameError.length > 0}
              value={lastName}
            />
          </Grid>
        </Grid>

        <Grid
          container
          size={{ xs: 12 }}
          direction="row"
          justifyContent={'center'}
        >
          <RoundBackgroundInput
            onChange={(e) => {
              setEmailError('');
              setEmail(e.target.value);
            }}
            onBlur={() => {
              if (!email) {
                return;
              }

              const trimmedEmail = email.trim();
              if (trimmedEmail !== email) {
                setEmail(trimmedEmail);
              }

              if (!validator.validEmail(trimmedEmail)) {
                if (!validator.emailHasAt(trimmedEmail)) {
                  setEmailError(t('emailMissingAt'));
                } else if (
                  validator.emailHasLocalPart(trimmedEmail) &&
                  !validator.emailHasDomain(trimmedEmail)
                ) {
                  setEmailError(t('emailMissingDomain'));
                } else if (validator.emailHasInvalidCharacters(trimmedEmail)) {
                  setEmailError(t('emailInvalidCharacters'));
                } else {
                  setEmailError(t('emailInvalidFallback'));
                }
              }
            }}
            error={emailError.length > 0}
            helperText={emailError}
            placeholder={t('email')}
            inputMode="email"
            value={email}
          />
        </Grid>

        <Grid
          container
          size={{ xs: 12 }}
          direction="column"
          justifyContent={'center'}
        >
          <RoundBackgroundInput
            placeholder={t('password')}
            type={passwordOn ? 'password' : undefined}
            onChange={(e) => {
              setPassword(e.target.value);
            }}
            InputProps={{
              endAdornment: (
                <Box
                  component="img"
                  src={
                    passwordOn
                      ? images.v2.inputs.passwordOn
                      : images.v2.inputs.passwordOff
                  }
                  className="hover:cursor-pointer"
                  onClick={() => setPasswordOn((prev) => !prev)}
                />
              ),
            }}
            value={password}
          />

          <PasswordStrengthView password={password} />
        </Grid>

        <Grid
          container
          size={{ xs: 12 }}
          direction="column"
          justifyContent={'center'}
          alignItems={'center'}
        >
          <FormControlLabel
            label={
              <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                {!isJa && (
                  <Typography variant="body1">
                    {t('iAgreeToAll')}&nbsp;
                  </Typography>
                )}

                <a target="_" href={termsURI} className="underline">
                  {isJa
                    ? t('iAgreeToAllTermsAndConditions')
                    : t('termsAndConditions')}
                </a>
              </Box>
            }
            control={
              <Checkbox
                checked={tcsAgreed}
                onChange={(_event, checked) => {
                  setTcsAgreed(checked);
                }}
              />
            }
          />
        </Grid>
      </Grid>
    </Fade>
  );
});

export default SignUpForm;
